// The acting org: its members and invitations (admins manage them), and
// for platform super-users the list of every org.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'

import { activeOrg, api, can, type Org } from '../api'
import { ErrorBox, Pill } from '../components'
import { dt } from '../format'
import { useNow } from '../hooks'

const ROLES = ['viewer', 'editor', 'admin'] as const

export function OrganisationPage() {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const q = useQuery({ queryKey: ['org'], queryFn: api.org })
  const now = useNow(60_000)
  const admin = can(me.data, 'admin')
  const refresh = () => qc.invalidateQueries({ queryKey: ['org'] })
  const role = useMutation({ mutationFn: (v: { user: number; role: string }) => api.setMemberRole(v.user, v.role), onSuccess: refresh })
  const remove = useMutation({ mutationFn: api.removeMember, onSuccess: refresh })
  const revoke = useMutation({ mutationFn: api.revokeInvite, onSuccess: refresh })
  const [email, setEmail] = useState('')
  const [newRole, setNewRole] = useState<string>('viewer')
  const invite = useMutation({
    mutationFn: () => api.invite(email, newRole),
    onSuccess: () => {
      setEmail('')
      refresh()
    },
  })

  if (q.error) return <p className="callout err">{q.error.message}</p>
  if (!q.data) return <p className="muted">Loading…</p>
  const { org, members, invitations } = q.data
  const open = invitations.filter((i) => !i.accepted_at && !i.revoked_at && Date.parse(i.expires_at) > now)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    invite.mutate()
  }

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>{org.name}</h1>
          <p className="sub">
            <span className="mono">{org.id}</span>
            {org.platform && ' · the platform org: staff are members through the Authentik groups aleva-viewer, aleva-editor and aleva-admin.'}
          </p>
        </div>
      </header>
      {[role.error, remove.error, revoke.error].map((e, i) => e && <ErrorBox key={i} error={e} />)}

      {!org.platform && (
        <div className="card">
          <h2>Members</h2>
          <table className="tbl">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Member since</th>
                <th>Last login</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.user_id}>
                  <td>{m.email}</td>
                  <td>
                    {admin ? (
                      <select aria-label={`Role of ${m.email}`} value={m.role} onChange={(e) => role.mutate({ user: m.user_id, role: e.target.value })}>
                        {ROLES.map((r) => (
                          <option key={r}>{r}</option>
                        ))}
                      </select>
                    ) : (
                      m.role
                    )}
                  </td>
                  <td>{dt(m.created_at)}</td>
                  <td>{m.last_login_at ? dt(m.last_login_at) : <span className="muted">never</span>}</td>
                  <td>
                    {admin && (
                      <button className="btn btn-sm" onClick={() => window.confirm(`Remove ${m.email} from ${org.name}?`) && remove.mutate(m.user_id)}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="note">An organisation always keeps at least one admin. Viewers read; editors also change sources, buckets and endpoints; admins also publish, manage API keys, integrations and members.</p>
        </div>
      )}

      {admin && !org.platform && (
        <div className="card">
          <h2>Invite</h2>
          <form className="row" onSubmit={submit}>
            <input type="email" className="grow" required placeholder="name@company.se" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <select aria-label="Role" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
              {ROLES.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
            <button className="btn btn-primary" type="submit" disabled={invite.isPending}>
              Send invitation
            </button>
          </form>
          {invite.error && <ErrorBox error={invite.error} />}
          <p className="note">The invitation link is valid for 7 days. People log in with emailed links; there are no passwords.</p>
          {open.length > 0 && (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Invited</th>
                  <th>Role</th>
                  <th>By</th>
                  <th>Expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {open.map((i) => (
                  <tr key={i.id}>
                    <td>{i.email}</td>
                    <td>{i.role}</td>
                    <td>{i.invited_by}</td>
                    <td>{dt(i.expires_at)}</td>
                    <td>
                      <button className="btn btn-sm" onClick={() => revoke.mutate(i.id)}>
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {me.data?.super && <AllOrgs current={me.data.org_id} />}
    </section>
  )
}

function AllOrgs({ current }: { current: string }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['orgs'], queryFn: api.orgs })
  const [f, setF] = useState({ id: '', name: '', email: '' })
  const create = useMutation({
    mutationFn: () => api.createOrg(f.id, f.name, f.email),
    onSuccess: () => {
      setF({ id: '', name: '', email: '' })
      qc.invalidateQueries({ queryKey: ['orgs'] })
    },
  })
  const toggle = useMutation({
    mutationFn: (o: Org) => api.setOrgDisabled(o.id, !o.disabled_at),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs'] }),
  })
  const actIn = (id: string) => {
    activeOrg.set(id === 'aleva' ? '' : id)
    qc.clear()
    window.location.assign('/')
  }
  const list = (q.data ?? []) as Org[]
  return (
    <div className="card">
      <h2>All organisations (super-user)</h2>
      <table className="tbl">
        <thead>
          <tr>
            <th>Org</th>
            <th>Members</th>
            <th>Created</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {list.map((o) => (
            <tr key={o.id}>
              <td>
                <span className="cell-two">
                  <span>{o.name}</span>
                  <span className="muted mono">{o.id}</span>
                </span>
              </td>
              <td className="num">{o.platform ? '–' : o.members}</td>
              <td>{dt(o.created_at)}</td>
              <td>{o.platform ? <Pill kind="neutral">platform</Pill> : o.disabled_at ? <Pill kind="err">disabled</Pill> : <Pill kind="ok">active</Pill>}</td>
              <td className="row">
                {o.id !== current && (
                  <button className="btn btn-sm" onClick={() => actIn(o.id)}>
                    Act in this org
                  </button>
                )}
                {!o.platform && (
                  <button className="btn btn-sm" disabled={toggle.isPending} onClick={() => toggle.mutate(o)}>
                    {o.disabled_at ? 'Enable' : 'Disable'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3>New organisation</h3>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault()
          create.mutate()
        }}
      >
        <input className="mono" required placeholder="id (acme)" aria-label="Org id" value={f.id} onChange={(e) => setF({ ...f, id: e.target.value })} />
        <input required placeholder="Name (Acme AB)" aria-label="Org name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <input type="email" className="grow" required placeholder="First admin's email" aria-label="Admin email" value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })} />
        <button className="btn btn-primary" type="submit" disabled={create.isPending}>
          Create and invite
        </button>
      </form>
      {create.error && <ErrorBox error={create.error} />}
      <p className="note">The id is permanent: lower case, digits and hyphens. Its endpoints live under /v1/o/&lt;id&gt;/.</p>
    </div>
  )
}
